// lib/qwen-image-21.ts
import type { QwenImage21BorderLock, QwenImage21Params, QwenImage21Resolution } from '#types/types';

export const QWEN_IMAGE_21_MODEL_ID = 'qwen-image-2.1';
// Our own cog, running comfyui/workflows/qwen21-edit-api.json with this file's math.
export const QWEN_IMAGE_21_REPLICATE_MODEL = 'fater-ai/region-lock-edit:d5ff48cf38658b35f3b31ff929e74ebb6bb2e7562f84fa7d60b46da1db2a2b18';

// Server env, chosen per environment: a failure on either runtime is never rerouted to the other.
export const shouldUseLocalQwen21 = (): boolean => process.env.COMFYUI_QWEN21_ENABLED?.trim().toLowerCase() === 'true';

export const QWEN_IMAGE_21_RESOLUTION_OPTIONS: QwenImage21Resolution[] = ['0.5 MP', '1 MP', '2 MP', '3 MP', '4 MP'];
export const QWEN_IMAGE_21_DEFAULT_RESOLUTION: QwenImage21Resolution = '1 MP';
export const QWEN_IMAGE_21_DEFAULT_STEPS = 25;
export const QWEN_IMAGE_21_MAX_STEPS = 50;
export const QWEN_IMAGE_21_DEFAULT_CFG = 1;
export const QWEN_IMAGE_21_MIN_CFG = 1;
export const QWEN_IMAGE_21_MAX_CFG = 4;
export const QWEN_IMAGE_21_MAX_REFERENCES = 9;
export const QWEN_IMAGE_21_DIMENSION_MULTIPLE = 32;
export const QWEN_IMAGE_21_MAX_SIDE = 2048;
// With resolution 0 the text encoder keeps references at native size: nine large photos saturate VRAM.
export const QWEN_IMAGE_21_REFERENCE_MAX_PIXELS = 1_000_000;
export const QWEN_IMAGE_21_MIN_NOISE_LEVEL = 0.9;
// Unvalidated guard: the main image the encoder reads is never shrunk below this long side.
export const QWEN_IMAGE_21_REFERENCE_MIN_SIDE = 256;

// ComfyUI samples Qwen 2.1 on ModelSamplingFlux: 10000 timesteps, mu 0.69. CFGOverride's percent is 1 - t.
const FLUX_TIMESTEPS = 10000;
const fluxSigma = (t: number) => Math.exp(0.69) / (Math.exp(0.69) + 1 / t - 1);

/** CFGOverride start_percent from which the last `cfgOneLastSteps` steps run at cfg 1; 1 overrides no step. */
export const resolveQwenImage21CfgOneStartPercent = (steps: number, cfgOneLastSteps: number): number => {
  if (cfgOneLastSteps <= 0) return 1;
  // ComfyUI's simple schedule
  const t = (step: number) => 1 - Math.floor(step * (FLUX_TIMESTEPS / steps)) / FLUX_TIMESTEPS;
  return 1 - (t(steps - cfgOneLastSteps - 1) + t(steps - cfgOneLastSteps)) / 2;
};

/** The cfg-1 tail, 0 to steps - 1, shortened until step 0, whose sigma SetFirstSigma lowers to noiseLevel, stays guided. */
export const resolveQwenImage21CfgOneLastSteps = (steps: number, requested: number, noiseLevel: number): number => {
  let lastSteps = Math.min(steps - 1, Math.max(0, Math.round(requested)));
  while (lastSteps > 0 && fluxSigma(1 - resolveQwenImage21CfgOneStartPercent(steps, lastSteps)) >= noiseLevel) lastSteps -= 1;
  return lastSteps;
};

/** The main image's scale for the encoder: the layers' pixels per world unit over the task's, never above 1. */
export const resolveQwenImage21ReferenceScale = (nativeDensity: number, upscaleFactor: number): number => (
  nativeDensity > 0 && upscaleFactor > 0 ? Math.min(1, nativeDensity / upscaleFactor) : 1
);

const RESOLUTION_MEGAPIXELS: Record<QwenImage21Resolution, number> = {
  '0.5 MP': 0.5,
  '1 MP': 1,
  '2 MP': 2,
  '3 MP': 3,
  '4 MP': 4,
};

/** The main image and the output latent share these dimensions: the ratio of the source, on the 32px grid, no side above 2048. */
export const resolveQwenImage21Dimensions = ({
  sourceWidth,
  sourceHeight,
  resolution,
}: {
  sourceWidth: number;
  sourceHeight: number;
  resolution: unknown;
}): { width: number; height: number } => {
  const megapixels = RESOLUTION_MEGAPIXELS[resolution as QwenImage21Resolution]
    ?? RESOLUTION_MEGAPIXELS[QWEN_IMAGE_21_DEFAULT_RESOLUTION];
  const valid = sourceWidth > 0 && sourceHeight > 0;
  const baseWidth = valid ? sourceWidth : 1;
  const baseHeight = valid ? sourceHeight : 1;
  const longSide = Math.max(baseWidth, baseHeight);
  const scale = Math.min(Math.sqrt((megapixels * 1_000_000) / (baseWidth * baseHeight)), QWEN_IMAGE_21_MAX_SIDE / longSide);
  // The epsilon absorbs float noise, so an exact 1152 does not floor to 1120.
  const toGrid = (value: number) => Math.max(
    QWEN_IMAGE_21_DIMENSION_MULTIPLE,
    Math.floor((value * scale) / QWEN_IMAGE_21_DIMENSION_MULTIPLE + 1e-9) * QWEN_IMAGE_21_DIMENSION_MULTIPLE,
  );
  return { width: toGrid(baseWidth), height: toGrid(baseHeight) };
};

/** A Canvas task's geometry: the latent dimensions, and the factor that maps them back onto the Generation Area. */
export const resolveQwenImage21TaskGeometry = (
  generationRect: { w: number; h: number },
  resolution: unknown,
): { width: number; height: number; upscaleFactor: number } => {
  const { width, height } = resolveQwenImage21Dimensions({ sourceWidth: generationRect.w, sourceHeight: generationRect.h, resolution });
  return { width, height, upscaleFactor: width / generationRect.w };
};

export const QWEN_IMAGE_21_BORDER_LOCK_PX_HALF_MP = 16;
export const QWEN_IMAGE_21_BORDER_LOCK_PX = 32;

type Qwen21Rect = { x: number; y: number; w: number; h: number };

/** Lock widths in task pixels: a side of the Generation Area on (or past) the frame edge stays free, the others keep the source. */
export const resolveQwenImage21BorderLock = (
  rect: Qwen21Rect,
  frameRect: Qwen21Rect,
  resolution: unknown,
  geometry: { width: number; height: number },
): QwenImage21BorderLock => {
  const lock = resolution === '0.5 MP' ? QWEN_IMAGE_21_BORDER_LOCK_PX_HALF_MP : QWEN_IMAGE_21_BORDER_LOCK_PX;
  // A very elongated area can be 32 px across: two full locks would leave nothing to generate.
  const horizontal = Math.min(lock, Math.floor((geometry.width - 1) / 2));
  const vertical = Math.min(lock, Math.floor((geometry.height - 1) / 2));
  return {
    left: rect.x <= frameRect.x + 1 ? 0 : horizontal,
    top: rect.y <= frameRect.y + 1 ? 0 : vertical,
    right: rect.x + rect.w >= frameRect.x + frameRect.w - 1 ? 0 : horizontal,
    bottom: rect.y + rect.h >= frameRect.y + frameRect.h - 1 ? 0 : vertical,
  };
};

/** Absent is all free; otherwise four non-negative integers that leave some of the image to generate. */
export const isQwenImage21BorderLockValid = (lock: unknown, width: number, height: number): boolean => {
  if (lock === undefined) return true;
  if (!lock || typeof lock !== 'object') return false;
  const { left, top, right, bottom } = lock as Record<string, unknown>;
  const sides = [left, top, right, bottom];
  if (!sides.every(side => typeof side === 'number' && Number.isInteger(side) && side >= 0)) return false;
  return (left as number) + (right as number) < width && (top as number) + (bottom as number) < height;
};

/** A latent side both runtimes accept: a positive multiple of 32, at most 2048. */
export const isQwenImage21GridSide = (value: unknown): boolean => typeof value === 'number' && Number.isInteger(value)
  && value > 0 && value <= QWEN_IMAGE_21_MAX_SIDE && value % QWEN_IMAGE_21_DIMENSION_MULTIPLE === 0;

/** The sampler settings both runtimes send: clamped, defaulted, the seed only when fixed, the reference scale only when given. */
export const resolveQwenImage21RunParams = (
  params: Pick<QwenImage21Params, 'steps' | 'cfg' | 'seed' | 'noiseLevel' | 'cfgOneLastSteps' | 'referenceScale'>,
): { steps: number; cfg: number; seed?: number; noiseLevel: number; cfgOneLastSteps: number; referenceScale?: number } => {
  const isNumber = (value: unknown): value is number => typeof value === 'number' && Number.isFinite(value);
  const steps = isNumber(params.steps) ? Math.min(QWEN_IMAGE_21_MAX_STEPS, Math.max(1, Math.round(params.steps))) : QWEN_IMAGE_21_DEFAULT_STEPS;
  const cfg = isNumber(params.cfg) ? Math.min(QWEN_IMAGE_21_MAX_CFG, Math.max(QWEN_IMAGE_21_MIN_CFG, params.cfg)) : QWEN_IMAGE_21_DEFAULT_CFG;
  const noiseLevel = isNumber(params.noiseLevel) ? Math.min(1, Math.max(QWEN_IMAGE_21_MIN_NOISE_LEVEL, params.noiseLevel)) : 1;
  return {
    steps,
    cfg,
    seed: isNumber(params.seed) && params.seed >= 0 ? Math.floor(params.seed) : undefined,
    noiseLevel,
    // At cfg 1 every step already skips the negative pass.
    cfgOneLastSteps: cfg > 1 && isNumber(params.cfgOneLastSteps) ? resolveQwenImage21CfgOneLastSteps(steps, params.cfgOneLastSteps, noiseLevel) : 0,
    referenceScale: isNumber(params.referenceScale) && params.referenceScale > 0 ? Math.min(1, params.referenceScale) : undefined,
  };
};

/**
 * The hosted cog's inputs. It resizes the main image to its own working size with no float epsilon,
 * so `megapixels` sits a hair above the target's area: that size is then exactly the target.
 */
export const buildQwenImage21ReplicateInput = ({
  params,
  inputImageUrls,
  targetWidth,
  targetHeight,
}: {
  params: Pick<QwenImage21Params,
    'prompt' | 'steps' | 'cfg' | 'seed' | 'borderLock' | 'negativePrompt' | 'noiseLevel' | 'cfgOneLastSteps' | 'referenceScale'>;
  inputImageUrls: string[];
  targetWidth: number;
  targetHeight: number;
}): Record<string, unknown> => {
  const [image, ...referenceImages] = inputImageUrls;
  const { steps, cfg, seed, noiseLevel, cfgOneLastSteps, referenceScale } = resolveQwenImage21RunParams(params);
  const { left = 0, top = 0, right = 0, bottom = 0 } = params.borderLock ?? {};
  return {
    image,
    prompt: params.prompt,
    negative_prompt: typeof params.negativePrompt === 'string' ? params.negativePrompt : '',
    ...(referenceImages.length > 0 ? { reference_images: referenceImages } : {}),
    seed: seed ?? -1,
    steps,
    cfg,
    cfg_one_last_steps: cfgOneLastSteps,
    noise_level: noiseLevel,
    ...(referenceScale !== undefined ? { reference_scale: referenceScale } : {}),
    megapixels: Math.min(4, Math.max(0.25, (targetWidth * targetHeight / 1_000_000) * (1 + 1e-6))),
    lock_left: left,
    lock_top: top,
    lock_right: right,
    lock_bottom: bottom,
  };
};
